import React from 'react';
import {
  ShieldCheck, Zap, Users, MapPin,
  Sparkles, CheckCircle2, HeartHandshake,
  Clock, Award, Building2, PhoneCall, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutSection = () => {
  const stats = [
    {
      value: "500,000+",
      label: "Delighted Travelers",
      sub: "Across all 28 Indian states",
      icon: Users,
      color: "#0d6efd",
      bg: "rgba(13, 110, 253, 0.08)"
    },
    {
      value: "15,000+",
      label: "Verified Accommodations",
      sub: "Hotels, resorts & heritage stays",
      icon: Building2,
      color: "#10b981",
      bg: "rgba(16, 185, 129, 0.08)"
    },
    {
      value: "120+",
      label: "Cities & Pilgrimage Hubs",
      sub: "Metro cities to holy circuits",
      icon: MapPin,
      color: "#f59e0b",
      bg: "rgba(245, 158, 11, 0.08)"
    },
    {
      value: "4.9 ★",
      label: "Traveler Satisfaction",
      sub: "Over 85,000+ authentic reviews",
      icon: Award,
      color: "#8b5cf6",
      bg: "rgba(139, 92, 246, 0.08)"
    }
  ];

  const pillars = [
    {
      icon: ShieldCheck,
      title: "Zero Surprise Pricing",
      desc: "What you see is exactly what you pay. Transparent rates with all taxes itemized upfront — zero hidden checkout convenience fees.",
      badge: "Transparent Always"
    },
    {
      icon: Zap,
      title: "50-Point Room Quality Audit",
      desc: "Every listed hotel undergoes a strict inspection for clean linens, working AC, hot water, sanitized washrooms, and high-speed Wi-Fi.",
      badge: "Quality Guaranteed"
    },
    {
      icon: Clock,
      title: "Instant Digital Confirmation",
      desc: "Direct API integration with hotel desks ensures your reservation is locked in immediately — eliminating double-booking worries.",
      badge: "Real-Time Sync"
    },
    {
      icon: HeartHandshake,
      title: "24/7 Human-First Concierge",
      desc: "Speak with our dedicated Indian travel desk anytime in English, Hindi, and regional languages for custom stays, group tours, or queries.",
      badge: "Live Travel Desk"
    }
  ];

  return (
    <div className="about-us-wrapper overflow-hidden">

      {/* ── 1. Hero Section ────────────────────────────────────────────── */}
      <section
        className="position-relative py-5 text-white overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #090e1a 0%, #0d172e 50%, #0a1124 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        {/* Ambient Glow Orbs */}
        <div
          className="position-absolute top-0 end-0 rounded-circle"
          style={{
            width: '450px',
            height: '450px',
            background: 'radial-gradient(circle, rgba(13, 110, 253, 0.15) 0%, rgba(13, 110, 253, 0) 70%)',
            transform: 'translate(20%, -20%)',
            pointerEvents: 'none'
          }}
        />
        <div
          className="position-absolute bottom-0 start-0 rounded-circle"
          style={{
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(-20%, 20%)',
            pointerEvents: 'none'
          }}
        />

        <div className="container position-relative py-4 py-lg-5" style={{ zIndex: 2 }}>
          <div className="text-center mx-auto" style={{ maxWidth: '820px' }}>

            {/* National Brand Pill */}
            <div
              className="d-inline-flex align-items-center gap-2 px-3.5 py-1.5 rounded-pill mb-4"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.18)'
              }}
            >
              <span style={{ fontSize: '14px' }}>🇮🇳</span>
              <span
                className="fw-bold"
                style={{ color: '#38bdf8', fontSize: '12px', letterSpacing: '0.6px', textTransform: 'uppercase' }}
              >
                Proudly Built in India • Next-Gen Travel Platform
              </span>
            </div>

            {/* Main Title */}
            <h1
              className="display-4 fw-black text-white mb-3"
              style={{ letterSpacing: '-1px', lineHeight: '1.2' }}
            >
              Smart, Seamless & Honest Stays Across India
            </h1>

            {/* Subtitle */}
            <p
              className="fs-5 mb-4 mx-auto"
              style={{ color: '#94a3b8', lineHeight: '1.7', maxWidth: '700px' }}
            >
              ZoyoTrip was founded with a singular purpose: to make booking verified, affordable, and safe hotels effortlessly simple for every Indian family, pilgrimage traveler, and modern explorer.
            </p>

            {/* Action Buttons */}
            <div className="d-flex flex-wrap justify-content-center gap-3 pt-2">
              <Link
                to="/search"
                className="btn btn-primary rounded-pill px-4 py-2.5 fw-bold d-inline-flex align-items-center gap-2 shadow-sm text-decoration-none hover-lift"
                style={{ minHeight: '48px', fontSize: '15px' }}
              >
                <span>Explore Stays</span>
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact-us"
                className="btn btn-outline-light rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2 text-decoration-none hover-lift"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                  minHeight: '48px',
                  fontSize: '15px'
                }}
              >
                <span>Contact Travel Desk</span>
              </Link>
            </div>

          </div>

          {/* ── Key Impact Stat Cards ── */}
          <div className="row g-3 g-md-4 mt-5 pt-2">
            {stats.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <div className="col-6 col-lg-3" key={idx}>
                  <div
                    className="p-3.5 p-md-4 rounded-4 h-100 transition hover-lift text-center text-lg-start"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-3 p-2.5 mb-3"
                      style={{ backgroundColor: item.bg, color: item.color }}
                    >
                      <IconComp size={22} />
                    </div>
                    <div className="fs-3 fw-black text-white mb-1" style={{ letterSpacing: '-0.5px' }}>
                      {item.value}
                    </div>
                    <div className="fw-bold text-light mb-1" style={{ fontSize: '14.5px' }}>
                      {item.label}
                    </div>
                    <div className="small" style={{ color: '#94a3b8', fontSize: '12px' }}>
                      {item.sub}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>


      {/* ── 2. Our Origin Story ────────────────────────────────────────── */}
      <section className="py-5 bg-white position-relative">
        <div className="container py-3 py-lg-4">
          <div className="row align-items-center g-5">

            {/* Left Image with Floating Trust Badge */}
            <div className="col-lg-6">
              <div className="position-relative mx-auto" style={{ maxWidth: '520px' }}>
                <div
                  className="rounded-4 overflow-hidden shadow-lg border"
                  style={{ maxHeight: '440px' }}
                >
                  <img
                    src={require('../Assets/Images/about.jpg')}
                    alt="ZoyoTrip Hospitality & Travel Experience"
                    className="img-fluid w-100 h-100 object-fit-cover hover-scale"
                    style={{ transition: 'transform 0.4s ease' }}
                  />
                </div>

                {/* Floating Experience Badge */}
                <div
                  className="position-absolute bottom-0 start-0 m-3 m-md-4 p-3 rounded-4 shadow-lg text-white d-flex align-items-center gap-3"
                  style={{
                    background: 'linear-gradient(135deg, #090e1a 0%, #111d38 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    maxWidth: '300px'
                  }}
                >
                  <div
                    className="p-2.5 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}
                  >
                    <CheckCircle2 size={24} style={{ color: '#10b981' }} />
                  </div>
                  <div>
                    <div className="fw-bold text-white small">100% Verified Stays</div>
                    <div className="text-white-50" style={{ fontSize: '11px' }}>
                      Inspected for genuine pictures, cleanliness & guaranteed check-in.
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Right Content */}
            <div className="col-lg-6">
              <div className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill bg-primary bg-opacity-10 text-primary fw-bold small mb-3">
                <Sparkles size={14} />
                <span>OUR STORY & PHILOSOPHY</span>
              </div>

              <h2 className="display-6 fw-black text-dark mb-3" style={{ letterSpacing: '-0.5px' }}>
                Built to Fix What Real Travelers Actually Care About
              </h2>

              <p className="text-secondary lh-lg mb-3" style={{ fontSize: '15px' }}>
                We launched <strong>ZoyoTrip</strong> after experiencing first-hand what millions of Indian travelers endure every week: reaching a hotel after an exhausting train or flight journey only to discover that the room looks nothing like the internet photos, surprise convenience charges added at checkout, or zero customer support when plans shift.
              </p>

              <p className="text-secondary lh-lg mb-4" style={{ fontSize: '15px' }}>
                We realized Indian travelers deserve better. Whether it's a family embarking on sacred Char Dham circuits, business travelers booking last-minute corporate rooms in Bengaluru or Mumbai, or weekend road-trippers heading to Goa — ZoyoTrip brings authentic transparency, fair rates, and dedicated 24/7 human care to every stay.
              </p>

              {/* Core Checkpoints */}
              <div className="d-flex flex-column gap-2.5 pt-1">
                <div className="d-flex align-items-center gap-2.5">
                  <div className="p-1 rounded-circle bg-success bg-opacity-10 text-success flex-shrink-0">
                    <CheckCircle2 size={16} />
                  </div>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14.5px' }}>
                    Direct hotel API synchronization — zero risk of overbooking
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2.5">
                  <div className="p-1 rounded-circle bg-success bg-opacity-10 text-success flex-shrink-0">
                    <CheckCircle2 size={16} />
                  </div>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14.5px' }}>
                    Transparent price breakdown with no hidden taxes or fees
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2.5">
                  <div className="p-1 rounded-circle bg-success bg-opacity-10 text-success flex-shrink-0">
                    <CheckCircle2 size={16} />
                  </div>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14.5px' }}>
                    Dedicated 24/7 travel desk with real human concierge care
                  </span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>


      {/* ── 3. Mission & Vision Dual Cards ─────────────────────────────── */}
      <section className="py-5 bg-light position-relative">
        <div className="container py-3">

          <div className="text-center mx-auto mb-5" style={{ maxWidth: '680px' }}>
            <h2 className="display-6 fw-bold text-dark mb-2">Our Mission & Vision</h2>
            <p className="text-muted fs-6">
              Guiding principles that drive how we design technology and treat every traveler who books with us.
            </p>
          </div>

          <div className="row g-4">

            {/* Mission Card */}
            <div className="col-12 col-md-6">
              <div
                className="p-4 p-lg-5 rounded-4 bg-white h-100 shadow-sm border transition hover-lift position-relative overflow-hidden"
                style={{ borderColor: '#e2e8f0' }}
              >
                <div
                  className="position-absolute top-0 end-0 p-4 opacity-10 text-primary"
                  style={{ pointerEvents: 'none' }}
                >
                  <Award size={90} />
                </div>

                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3 p-3 mb-4 text-primary"
                  style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}
                >
                  <Award size={28} />
                </div>

                <h3 className="fw-bold text-dark mb-3 fs-3">Our Mission</h3>
                <p className="text-secondary lh-lg mb-0" style={{ fontSize: '15.5px' }}>
                  To democratize dependable, safe, and transparent hospitality across India by equipping travelers with truthful room data, instant reservations, and compassionate 24/7 human guidance at every step of their journey.
                </p>
              </div>
            </div>

            {/* Vision Card */}
            <div className="col-12 col-md-6">
              <div
                className="p-4 p-lg-5 rounded-4 bg-white h-100 shadow-sm border transition hover-lift position-relative overflow-hidden"
                style={{ borderColor: '#e2e8f0' }}
              >
                <div
                  className="position-absolute top-0 end-0 p-4 opacity-10 text-success"
                  style={{ pointerEvents: 'none' }}
                >
                  <HeartHandshake size={90} />
                </div>

                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3 p-3 mb-4 text-success"
                  style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                >
                  <HeartHandshake size={28} />
                </div>

                <h3 className="fw-bold text-dark mb-3 fs-3">Our Vision</h3>
                <p className="text-secondary lh-lg mb-0" style={{ fontSize: '15.5px' }}>
                  To become India’s most admired hospitality ecosystem — bridging heritage spiritual towns, remote leisure retreats, and bustling commercial corridors into one seamless, trustworthy booking platform.
                </p>
              </div>
            </div>

          </div>

        </div>
      </section>


      {/* ── 4. The 4 Pillars of ZoyoTrip Quality ────────────────────────── */}
      <section className="py-5 bg-white position-relative">
        <div className="container py-3 py-lg-4">

          <div className="text-center mx-auto mb-5" style={{ maxWidth: '680px' }}>
            <div className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill bg-success bg-opacity-10 text-success fw-bold small mb-3">
              <ShieldCheck size={14} />
              <span>THE ZOYOTRIP STANDARD</span>
            </div>
            <h2 className="display-6 fw-bold text-dark mb-2">Built on Trust, Delivered with Precision</h2>
            <p className="text-muted fs-6">
              Every feature on our platform is designed to eliminate uncertainty and maximize peace of mind.
            </p>
          </div>

          <div className="row g-4">
            {pillars.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <div className="col-12 col-md-6 col-lg-3" key={idx}>
                  <div
                    className="p-4 rounded-4 h-100 border transition hover-lift d-flex flex-column"
                    style={{
                      backgroundColor: '#ffffff',
                      borderColor: '#e2e8f0',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div
                        className="p-2.5 rounded-3 text-primary d-inline-flex align-items-center justify-content-center"
                        style={{ backgroundColor: 'rgba(13, 110, 253, 0.08)' }}
                      >
                        <IconComp size={24} />
                      </div>
                      <span
                        className="badge bg-light text-secondary rounded-pill px-2.5 py-1"
                        style={{ fontSize: '11px', fontWeight: '600' }}
                      >
                        {item.badge}
                      </span>
                    </div>

                    <h5 className="fw-bold text-dark mb-2">{item.title}</h5>
                    <p className="text-secondary small lh-base mb-0 flex-grow-1">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ── Styles ── */}
      <style>{`
        .hover-lift {
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .hover-lift:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
        }
        .hover-scale:hover {
          transform: scale(1.03);
        }
      `}</style>

    </div>
  );
};

export default AboutSection;